import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { ProgressBar } from "primereact/progressbar";
import "../../styles/generalInformation.pc.css";
import CloseBlockWindow from "../../../assets/arrowCloseWindow.svg";
import OpenBlock from "../../../assets/openBlock.svg";
import GraphRenderer, { REPORT_LIST } from "../../GraphRenderer/GraphRenderer";
import { Context } from "../../../index";
import { TabPanel, TabView } from "primereact/tabview";
import { AppContext } from "../../../App";
import { Dropdown } from "primereact/dropdown";
import StatisticsService from "../../../services/StatisticsService";
import "./GeneralInformation.css";
import { fullNameFormat } from "../../../helpers/Inicials";
import { ISideBarBoard, ISideBarProject, ISideBarSubproject } from "../../../common/api_interfaces/common/ISideBar";
// import { IBurnDownInfo, IBurnDownRequest, IGetTaskData, IOverdueTask } from "../../GraphRenderer/GraphTypes";
import Button from "../../UiLib/Button/Button";
import { Table, TableBody, TableHead, TableTh, TableTr } from "../../UiLib/Table/Table";
import CheckboxIos from "../../UiLib/CheckboxIos/CheckboxIos";
import GanttChart from "../../GanttRender/GanttRender";
import { ProjectContext } from "../../../pages/Project/Project";
import SelectDropdown from "../../Shared/SelectDropdown/SelectDropdown";
import { IGetTaskData } from "../../../common/api_interfaces/statistics/IGetTaskData";
import { IBurn } from "../../../common/api_interfaces/statistics/IBurn";
import { IOverdueTask } from "../../../common/api_interfaces/statistics/IOverdueTask";
import { IProjectProgressInfo } from "../../../common/api_interfaces/project/IProjectProgressInfo";

interface IProjectProgressProps {
    data: IProjectProgressInfo | undefined;
    projectId: number;
}

interface ISideBarBoardWithGraphParent extends ISideBarBoard {
    graph_parent?: number;
}
interface ISideBarProjectWithGraphParent extends ISideBarProject {
    graph_parent?: number;
}
interface ISideBarSubProjectWithGraphParent extends ISideBarSubproject {
    graph_parent?: number;
}
interface IOverdueTasksDisplay {
    id: number;
    name: string;
    datestart: string | undefined;
    dateend: string | undefined;
    remaininghours: number;
    completionpercent: number;
    responsible: string;
    rolename: string;
}

const RenderProgressBar = (
    data: any,
    currentBoard: any | undefined,
    currentProject: ISideBarProjectWithGraphParent | ISideBarSubProjectWithGraphParent | undefined
) => {
    let progressPercent = data?.progressPercent ?? 0;
    let projectName = currentProject?.name || "Проект не был найден";

    return (
        <div className="progress-bar-block">
            <div
                className="progress-bar-render"
                style={{
                    width: `${progressPercent}%`
                }}
            >
                <div className="bubbles">
                    {[...Array(5)].map((_, index) => (
                        <div key={index} className="bubble" />
                    ))}
                </div>
            </div>
            <div className="new_bar" style={{ width: "fit-content", overflow: "visible" }}>
                <div className="h2_20-black" style={{}}>
                    <span
                        style={{
                            whiteSpace: "nowrap",
                            height: "100%",
                            display: "block",
                            maxHeight: "72px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                        }}
                    >
                        {projectName}
                    </span>
                </div>
                <div style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                    {currentBoard?.name || ""}
                </div>
                <div className="progress-bar-percent">{progressPercent}%</div>
            </div>
        </div>
    );
};

const GeneralInformation: React.FC<IProjectProgressProps> = ({ data, projectId }) => {
    const { store } = useContext(Context);
    const { sideBarProjects } = useContext(AppContext);
    const { projectSettings } = useContext(ProjectContext);
    const isCollapsed = store.leftMenuCollapsed;
    const [progressInfo, setProgressInfo] = useState<IProjectProgressInfo | undefined>(undefined);
    const [openOnFullWindow, setOpenOnFullWindow] = useState(false);
    const [currentReport, setCurrentReport] = useState<REPORT_LIST>(REPORT_LIST.task_scheduler);
    const [currentGraph, setCurrentGraph] = useState<any>(undefined);
    const [availableProjects, setAvailableProjects] = useState<
        (ISideBarProjectWithGraphParent | ISideBarSubProjectWithGraphParent)[]
    >([]);
    const [currentProject, setCurrentProject] = useState<
        ISideBarProjectWithGraphParent | ISideBarSubProjectWithGraphParent | undefined
    >(undefined);
    const [availableBoards, setAvailableBoards] = useState<ISideBarBoardWithGraphParent[]>([]);
    const [currentBoard, setCurrentBoard] = useState<ISideBarBoardWithGraphParent | undefined>(undefined);
    const [warningMsg, setWarningMsg] = useState<string | undefined>(undefined);
    const [invalidTasks, setInvalidTasks] = useState<any[]>([]);
    const [showSecret, setShowSecret] = useState(false);
    const [includeProxyTask, setIncludeProxyTask] = useState(false);
    const [testData, setTestData] = useState<IGetTaskData[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [fakeData, setFakeData] = useState<IBurn>({
        actualHours: [
            { date: "2025-01-01", hours: -20 },
            { date: "2025-01-02", hours: -30 },
            { date: "2025-01-03", hours: -35 },
            { date: "2025-01-04", hours: 40 },
            { date: "2025-01-05", hours: 50 },
            { date: "2025-01-06", hours: 60 },
            { date: "2025-01-07", hours: 80 },
            { date: "2025-01-08", hours: 90 },
            { date: "2025-01-09", hours: 130 },
            { date: "2025-01-10", hours: 190 },
            { date: "2025-01-11", hours: 200 },
            { date: "2025-01-12", hours: 220 },
        ],
        idealHours: [
            { date: "2025-01-01", hours: 30 },
            { date: "2025-01-12", hours: 150 },
            { date: "2025-01-16", hours: -10 },
        ],
    });

    // Scroll to widget on full screen open
    useEffect(() => {
        const widget = containerRef.current;
        if (openOnFullWindow && widget) {
            widget?.scrollIntoView({ behavior: "auto", block: "center" });
        }
    }, [openOnFullWindow]);

    const getTestData = async () => {
        let data: IGetTaskData[] = (
            await StatisticsService.getTestData({
                projectId: currentProject!.id,
                boardId: currentBoard!.id,
                includeProxyTask: includeProxyTask,
            })
        ).data;
        setTestData(data);
    };

    const parseProjectStructure = useCallback(() => {
        let allProjectsFlat: (ISideBarProjectWithGraphParent | ISideBarSubProjectWithGraphParent)[] = [];
        let allBoardsFlat: ISideBarBoardWithGraphParent[] = [];

        for (let project of sideBarProjects as ISideBarProject[]) {
            allProjectsFlat.push({ ...project });

            for (let board of (project.boards as ISideBarBoardWithGraphParent[]) ?? []) {
                const boardCopy = { ...board, graph_parent: project.id };
                allBoardsFlat.push(boardCopy);

                for (let sub_board of (board.boards as ISideBarBoardWithGraphParent[]) ?? []) {
                    const subBoardCopy = { ...sub_board, graph_parent: project.id };
                    allBoardsFlat.push(subBoardCopy);
                }
            }

            if ("subprojects" in project && project.subprojects) {
                for (let sub_project of (project.subprojects as ISideBarSubproject[]) ?? []) {
                    const subProjectCopy = { ...sub_project, graph_parent: project.id };
                    allProjectsFlat.push(subProjectCopy);

                    for (let board of (sub_project.boards as ISideBarBoardWithGraphParent[]) ?? []) {
                        const boardCopy = { ...board, graph_parent: sub_project.id };
                        allBoardsFlat.push(boardCopy);

                        for (let sub_board of (board.boards as ISideBarBoardWithGraphParent[]) ?? []) {
                            const subBoardCopy = { ...sub_board, graph_parent: sub_project.id };
                            allBoardsFlat.push(subBoardCopy);
                        }
                    }
                }
            }
        }

        allProjectsFlat = allProjectsFlat.filter((xx) => xx.id === projectId || xx.graph_parent === projectId);
        const thisProjectBoards = allBoardsFlat.filter((xx) => xx.graph_parent === projectId);

        setAvailableProjects(allProjectsFlat);
        setAvailableBoards(allBoardsFlat);
        setCurrentProject(allProjectsFlat[0]);
        setCurrentBoard(thisProjectBoards[0]);
    }, [sideBarProjects, projectId]);

    const graphWarning = () => {
        if (invalidTasks.length) {
            return (
                <div className="graph-warning-block">
                    <p style={{ fontWeight: "bold", marginBottom: "8px" }}>⚠️ {warningMsg}</p>
                    Заполните задачи:{" "}
                    {invalidTasks.map((task) => (
                        <a key={task.id} href={`/task/${task.id}`}>
                            {task.name},
                        </a>
                    ))}
                </div>
            );
        } else {
            setInvalidTasks([])
        }
        return null;
    };

    const buildOverdueTasks = (rawData: IOverdueTask[]) => {
        const display_tasks: IOverdueTasksDisplay[] = rawData.map((task) => ({
            id: task.id,
            name: task.name,
            datestart: convertDate(task.dateStart),
            dateend: convertDate(task.dateEnd),
            remaininghours: (task.remainingHours ?? 0) / 60,
            completionpercent: task.completionPercent ?? 0,
            responsible: task.responsible ? fullNameFormat(task.responsible, "s N M") : "-",
            rolename: task.roleName,
        }));

        if (!display_tasks.length) return null;

        return (
            <table id="report-special-table">
                <thead className="custom_table_thead">
                    <tr>
                        <th className="user-monitor-toolbar-left-part-text">ЗАДАЧА</th>
                        <th className="user-monitor-toolbar-left-part-text">НАЧАЛО</th>
                        <th className="user-monitor-toolbar-left-part-text">ОКОНЧАНИЕ</th>
                        <th className="user-monitor-toolbar-left-part-text" style={{ width: "200px" }}>
                            ОСТАВШИЕСЯ ТРУДОЗАТРАТЫ, Ч
                        </th>
                        <th className="user-monitor-toolbar-left-part-text">% ЗАВЕРШЕНИЯ</th>
                        <th className="user-monitor-toolbar-left-part-text">ИСПОЛНИТЕЛЬ</th>
                        <th className="user-monitor-toolbar-left-part-text">РОЛЬ</th>
                    </tr>
                </thead>
                <tbody>
                    {display_tasks.map((task, index) => {
                        const highlightRow = index % 2 === 0;
                        return (
                            <tr key={task.id} className={`${highlightRow ? "table-row-striped-highlight" : ""}`}>
                                <td>
                                    <div className="td-inner-limiter">
                                        <a href={`/task/${task.id}`}>{task.name}</a>
                                    </div>
                                </td>
                                <td>{task.datestart ?? "-"}</td>
                                <td>{task.dateend ?? "-"}</td>
                                <td>{Number(task.remaininghours).toFixed(2)}</td>
                                <td>{task.completionpercent}</td>
                                <td>
                                    <div className="td-inner-limiter">{task.responsible ?? ""}</div>
                                </td>
                                <td>
                                    <div className="td-inner-limiter">{task.rolename}</div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const LoadGraph = useCallback(
        async (currentGraph: REPORT_LIST) => {
            if (!currentProject) return;
            setInvalidTasks([]);

            try {
                const data = await StatisticsService.getStatisticsData({
                    projectId: currentProject.id,
                    boardId: currentBoard?.id,
                    includeProxyTask: !currentBoard?.isProxyBoard ? includeProxyTask : false,
                });
                setProgressInfo(data?.data);
            } catch (err: any) {
                setWarningMsg(err?.response?.data?.message);
                setInvalidTasks(err?.response?.data?.invalidTasks ?? []);
                setCurrentGraph(undefined);
            }
            switch (currentGraph) {
                case REPORT_LIST.task_scheduler:
                    try {
                        await StatisticsService.reportTasksStatus({
                            projectId: currentProject.id,
                            boardId: currentBoard?.id,
                            includeProxyTask: !currentBoard?.isProxyBoard ? includeProxyTask : false,
                        });
                    } catch (err: any) {
                        setWarningMsg(err?.response?.data?.message);
                        setInvalidTasks(err?.response?.data?.invalidTasks ?? []);
                        setCurrentGraph(undefined);
                    }
                    setCurrentGraph(
                        <div className="project-gant-module">
                            <GanttChart
                                projectStartDate={data?.startDate}
                                responsibleRequiredOnCreate={projectSettings.responsibleRequired}
                                projectId={projectId}
                                boardId={currentBoard?.id}
                            />
                        </div>
                    );
                    break;
                case REPORT_LIST.burn_down:
                    try {
                        const data = await StatisticsService.getBurndown({
                            projectId: currentProject.id,
                            boardId: currentBoard?.id,
                            includeProxyTask: !currentBoard?.isProxyBoard ? includeProxyTask : false,
                        });
                        setInvalidTasks([]);

                        setCurrentGraph(
                            <GraphRenderer
                                key={`graph-${includeProxyTask}`}
                                rawData={data?.data ? { ...data.data } : null}
                                reportType={currentReport}
                                secretDebug={showSecret}
                                board={currentBoard?.id}
                                project={currentProject.id}
                                setFakeData={setFakeData}
                            />
                        );
                    } catch (err: any) {
                        setWarningMsg(err?.response?.data?.message);
                        setInvalidTasks(err?.response?.data?.invalidTasks ?? []);
                        setCurrentGraph(undefined);
                    }
                    break;

                case REPORT_LIST.burn_up:
                    try {
                        const data = await StatisticsService.getBurnup({
                            projectId: currentProject.id,
                            boardId: currentBoard?.id,
                            includeProxyTask: !currentBoard?.isProxyBoard ? includeProxyTask : false,
                        });
                        setInvalidTasks([]);

                        setCurrentGraph(
                            <GraphRenderer
                                key={`graph-${includeProxyTask}`}
                                rawData={data?.data ? { ...data.data } : null}
                                reportType={currentReport}
                                secretDebug={showSecret}
                                board={currentBoard?.id}
                                project={currentProject.id}
                                setFakeData={setFakeData}
                            />
                        );
                    } catch (err: any) {
                        setWarningMsg(err?.response?.data?.message);
                        setInvalidTasks(err?.response?.data?.invalidTasks ?? []);
                        setCurrentGraph(undefined);
                    }
                    break;

                case REPORT_LIST.overdue_tasks:
                    try {
                        const tasksStatus = await StatisticsService.reportTasksStatus({
                            projectId: currentProject.id,
                            boardId: currentBoard?.id,
                            includeProxyTask: !currentBoard?.isProxyBoard ? includeProxyTask : false,
                        });
                        const overdueData = await StatisticsService.reportGetOverdueTasks({
                            projectId: currentProject.id,
                            boardId: currentBoard?.id,
                            includeProxyTask: !currentBoard?.isProxyBoard ? includeProxyTask : false,
                        });
                        setInvalidTasks([]);

                        setCurrentGraph(
                            <>
                                <div className="special-pie-graph-container">
                                    <GraphRenderer
                                        key={`graph-tasks-${includeProxyTask}`}
                                        rawData={tasksStatus?.data ? [...tasksStatus.data] : []}
                                        reportType={currentReport}
                                        secretDebug={showSecret}
                                        board={currentBoard?.id}
                                        project={currentProject.id}
                                        setFakeData={setFakeData}
                                    />
                                </div>
                                <div className="report-special-table-container">
                                    <div className="report-special-table-wrapper">
                                        {buildOverdueTasks(overdueData?.data)}
                                    </div>
                                </div>
                            </>
                        );
                    } catch (err: any) {
                        setWarningMsg(err?.response?.data?.message);
                        setInvalidTasks(err?.response?.data?.invalidTasks ?? []);
                        setCurrentGraph(undefined);
                    }
                    break;
                default:
                    return;
            }
        },
        [currentProject, currentBoard, currentReport, showSecret, includeProxyTask]
    );

    useEffect(() => {
        parseProjectStructure();
    }, [parseProjectStructure]);

    useEffect(() => {
        if (openOnFullWindow) {
            LoadGraph(currentReport);
        }
    }, [openOnFullWindow, currentReport, LoadGraph]);

    const handleProjectChange = (projectId: number) => {
        const foundProject = availableProjects.find((xx) => xx.id === projectId);
        setCurrentProject(foundProject);
        setCurrentBoard(availableBoards.find((xx) => xx.graph_parent === foundProject?.id));
    };

    const handleBoardChange = (boardId: number) => {
        const foundBoard = availableBoards.find((xx) => xx.id === boardId);
        setCurrentBoard(foundBoard);
    };

    const RenderFullMode = (data: any) => {
        return (
            <div className="all-data-view-block">
                <div className="main-container">
                    <div className="open-block-header-wrapper">
                        <div className="big-fat-progress-bar-wrapper">
                            {RenderProgressBar(progressInfo, currentBoard, currentProject)}
                        </div>
                        <div className="filters-and-selectors-wrapper">
                            <SelectDropdown
                                enableSearch={false}
                            >
                                <SelectDropdown.Selected>
                                    <div>{availableProjects.find(xx => xx.id === currentProject?.id)?.name}</div>
                                </SelectDropdown.Selected>
                                {availableProjects.map((project) => (
                                        <SelectDropdown.Option>
                                            <div 
                                                id={`project-${project.id}`} 
                                                onClick={() => {handleProjectChange(project.id);}} 
                                                className="current-project-choise"
                                                style={project.id === currentProject?.id ? {backgroundColor: "var(--base-branded-blue-100)"} : {}}    
                                            >
                                                <a>{project.name}</a>
                                            </div>
                                        </SelectDropdown.Option>
                                    ))}
                            </SelectDropdown>
                            <SelectDropdown
                                enableSearch={false}
                            >
                                <SelectDropdown.Selected>
                                    <div>{availableBoards.find(xx => xx.id === currentBoard?.id)?.name}</div>
                                </SelectDropdown.Selected>
                                {[{label: "Нет доски",value: undefined}, ...availableBoards
                                    .filter((item) => item.graph_parent === currentProject?.id)
                                    .map((item) => ({
                                        label: item.name,
                                        value: item.id,
                                    })),
                                ].map(board => (
                                    <SelectDropdown.Option>
                                        <div 
                                            id={`pboard-${board.value}`} 
                                            onClick={() => {handleBoardChange(board.value ?? 0);}} 
                                            className="current-project-choise"
                                            style={board.value === currentBoard?.id ? {backgroundColor: "var(--base-branded-blue-100)"} : {}}    
                                        >
                                            <a>{board.label}</a>
                                        </div>
                                    </SelectDropdown.Option>
                                ))}
                            </SelectDropdown>
                            {currentBoard?.isProxyBoard || currentBoard === undefined ? null : (
                                <div className="proxy-btn-container">
                                    <p id={`${currentBoard}`}>
                                        Учитывать задачи с прокси
                                    </p>
                                    <CheckboxIos
                                        callBack={() => setIncludeProxyTask(!includeProxyTask)}
                                        checked={includeProxyTask}
                                    />
                                </div>
                            )}
                            {showSecret && (
                                <div>
                                    Test JSON
                                    <input
                                        style={{ height: "30px", width: "30px" }}
                                        id="super-duper-graph-test-check"
                                        type="checkbox"
                                    />
                                    <input
                                        id="super-duper-graph-test-input"
                                        type="text"
                                        defaultValue={JSON.stringify(fakeData)}
                                    />
                                    <br />
                                </div>
                            )}
                        </div>
                        <div className="min-mode-info-wrapper">
                            {RenderMiniMode(progressInfo)}
                        </div>
                    </div>
                </div>
                <div className="tab-container">
                    <TabView activeIndex={currentReport} onTabChange={(e) => setCurrentReport(e.index)}>
                        <TabPanel header="Планировщик задач"></TabPanel>
                        <TabPanel header="Просроченные задачи"></TabPanel>
                        <TabPanel header="Burn Down"></TabPanel>
                        <TabPanel header="Burn Up"></TabPanel>
                    </TabView>
                </div>
                <div className="info-container">
                    <div className="genInfo-bigmode-body-wrapper">
                        {invalidTasks.length > 0 ? graphWarning() : null}
                        <div className="plot-block-wrapper">
                            <div className="graph-wrapper">
                                {currentGraph}
                            </div>
                        </div>
                    </div>
                    {showSecret && (
                        <div>
                            <Button callBack={getTestData} text={"Получение данных построения графика"} />
                            {testData.length > 0 && (
                                <Table>
                                    <TableHead>
                                        <TableTr>
                                            <TableTh>ID</TableTh>
                                            <TableTh>Номер тикета</TableTh>
                                            <TableTh>Наименование</TableTh>
                                            <TableTh>Дата создания</TableTh>
                                            <TableTh>Дата начала</TableTh>
                                            <TableTh>Дата окончания</TableTh>
                                            <TableTh>Запланировано часов</TableTh>
                                            <TableTh>Дата смена статуса</TableTh>
                                            <TableTh>Старый статус</TableTh>
                                            <TableTh>Новый статус</TableTh>
                                            <TableTh>Актуальный статус</TableTh>
                                        </TableTr>
                                    </TableHead>
                                    <TableBody>
                                        {testData.map((xx) => (
                                            <TableTr key={xx.id}>
                                                <TableTh>{xx.id}</TableTh>
                                                <TableTh>{xx.idForView}</TableTh>
                                                <TableTh>{xx.shortDescription}</TableTh>
                                                <TableTh>{xx.createdAt}</TableTh>
                                                <TableTh>{xx.startDate}</TableTh>
                                                <TableTh>{xx.endDate}</TableTh>
                                                <TableTh>{(xx.allottedTime / 60).toFixed(1)}</TableTh>
                                                <TableTh>{xx.dateOfChangeStatus}</TableTh>
                                                <TableTh>{xx.oldStatusId}</TableTh>
                                                <TableTh>{xx.newStatusId}</TableTh>
                                                <TableTh>{xx.actualStatusId}</TableTh>
                                            </TableTr>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    )}
                </div>
            </div>
            // <>
            //     

            //     
            // </>
        );
    };

    const RenderMiniMode = (data: any, showSmallProgressBar?: boolean) => {
        return (
            <div className="genInfo_body_scroll">
                <div className="genInfo_body">
                    <div className="genInfo_body_item">
                        <p className="genInfo_body_item_p11 p_gray">
                            НАЧАЛО/ОКОНЧАНИЕ {openOnFullWindow ? "ПЕРИОДА" : "ПРОЕКТА"}
                        </p>
                        <div style={{ width: "16px" }}></div>
                        <p className="genInfo_body_item_p11 p_blue">
                            {toDisplayDate(data?.startDate)}/{data?.endDate ? toDisplayDate(data?.endDate) : ""}
                        </p>
                    </div>
                    <div className="genInfo_body_item">
                        <p className="genInfo_body_item_p11 p_gray">ЗАДАЧ В РАБОТЕ</p>
                        <div style={{ width: "16px" }}></div>
                        <p className="genInfo_body_item_p11 p_blue">{data?.tasksInProgress}</p>
                    </div>
                    <div className="genInfo_body_item">
                        <p className="genInfo_body_item_p11 p_gray">ВЫПОЛНЕНО ЗАДАЧ</p>
                        <div style={{ width: "16px" }}></div>
                        <p className="genInfo_body_item_p11 p_blue">{data?.tasksCompleted}</p>
                    </div>
                    <div className="genInfo_body_item">
                        <p className="genInfo_body_item_p11 p_gray">ПРОСРОЧЕНО ЗАДАЧ</p>
                        <div style={{ width: "16px" }}></div>
                        <p className="genInfo_body_item_p11 p_red">{data?.tasksOverdue}</p>
                    </div>
                    <div className="genInfo_body_item">
                        <p className="genInfo_body_item_p11 p_gray">ПРОГРЕСС</p>
                        <div style={{ width: "16px" }}></div>
                        <p className="genInfo_body_item_p11 p_blue">{data?.progressPercent}%</p>
                    </div>
                    {showSmallProgressBar && <ProgressBar value={data?.progressPercent ?? 0}></ProgressBar>}
                </div>
            </div>
        );
    };

    return (
        <div
            id="project-progress-info-widget-component"
            className={`project-files-widget-component ${
                openOnFullWindow
                    ? isCollapsed
                        ? "widget_wrapper--full__window collapse"
                        : "widget_wrapper--full__window"
                    : "widget_wrapper"
            }`}
            ref={containerRef}
        >
            <div className={`${openOnFullWindow ? "full-screen-vertical-flex widget_wrapper-full__window--block fit_content_variant" : ""} project-block-size`}>
                <div className="widgets_header--container">
                    {openOnFullWindow ? (
                        <div onClick={() => setOpenOnFullWindow(false)} className="widgets_header--open__block">
                            <img src={CloseBlockWindow} alt="close-block-window" />
                        </div>
                    ) : (
                        <div onClick={() => setOpenOnFullWindow(true)} className="widgets_header--open__block">
                            <img src={OpenBlock} alt="open-block" />
                        </div>
                    )}
                </div>
                <div style={{height: "25px"}}>
                    <div className="widget_header">
                        <div className="widjets_header_left">
                            <h2 className="widget-card-header-style" onClick={() => setShowSecret(!showSecret)}>
                                Общие сведения
                            </h2>
                        </div>
                    </div>
                </div>
                {openOnFullWindow ? RenderFullMode(progressInfo) : RenderMiniMode(data, true)}
            </div>
        </div>
    );
};

export default observer(GeneralInformation);

function convertDate(dateStr: string): string | undefined {
    if (!dateStr) return;
    const [year, month, day] = dateStr.split("-");
    return `${day}.${month}.${year}`;
}

function toDisplayDate(dateStr: string): string {
    try {
        const date = new Date(dateStr);
        const pad = (v: number) => v.toString().padStart(2, "0");
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
    } catch {
        return "-";
    }
}
